import { Box, Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import { DateTime } from 'luxon'
import React from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { checkIfSelfPaySource } from 'src/participant/participantUtils'
import { usePatientSource } from 'src/participant/usePatientSource'
import TreatmentPlanFields from 'src/participantHistory/TreatmentPlanFields'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { Loader } from 'src/designSystem'
import { DraftIndicator } from 'src/designSystem/DraftIndicator'
import ConfirmNoteDialog from './ConfirmNoteDialog'
import SubmitButton from './SubmitButton'
import { clearFormFromLocalStorage } from './persistFormHelpers'
import FormPersistor from './FormPersistor'
import { createTreatmentPlanFormSchema, TreatmentPlanFormType } from './TreatmentPlanFormSchema'
import { useLocalStorageWithPrefillValues } from './useLocalStorageWithPrefillValues'

const getInitialValues = () => ({
  presentingProblem: '',
  treatmentGoals: '',
  billingCodes: [],
  interventionsAndEngagementPlan: {
    participateTreatmentProgram: true,
    followupTelehealthSessions: true,
    utilizeCommunicationChannels: true,
    participantAgreement: true,
    additionalResources: '',
    coordinationWithExternalProviders: '',
    other: '',
  },
  obstaclesAndActionPlan: '',
})

const FORM_TYPE = PatientHistoryType.TreatmentPlan

const MISSING_DIAGNOSIS_TITLE = 'Diagnosis code missing'
const MISSING_DIAGNOSIS_CONTENTS =
  'Self-pay treatment plans can be saved without a diagnosis/billing code, but the diagnosis is still part of the clinical record. Proceed only if this is intentional.'

const TreatmentPlanForm = ({ uid, afterSubmittedCallback }: { uid: string; afterSubmittedCallback?: () => void }) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  const { values: initialValues, status } = useLocalStorageWithPrefillValues(uid, FORM_TYPE, getInitialValues)
  const { source, loading: sourceLoading } = usePatientSource(uid)
  const isSelfPay = checkIfSelfPaySource(source)

  if (status === 'loading') {
    return <Loader size="large" />
  }

  return (
    <Grid item>
      <Formik<TreatmentPlanFormType>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(
          // While the source is still loading the submit button stays disabled, so requiring codes
          // by default cannot block anyone — it only avoids briefly accepting a plan without one.
          createTreatmentPlanFormSchema({ requireBillingCodes: !isSelfPay })
        )}
        onSubmit={async (values, { resetForm }) => {
          await addPatientHistoryEntry({
            uid,
            date: DateTime.now().toFormat('yyyy-MM-dd'),
            type: PatientHistoryType.TreatmentPlan,
            value: values,
          })

          resetForm({ values: getInitialValues() })
          clearFormFromLocalStorage(uid, FORM_TYPE)
          afterSubmittedCallback?.()
        }}
      >
        {({ isSubmitting, dirty, values, submitForm }) => {
          // Everyone else has the code enforced by the validation schema, which reports it on the
          // field. Self-pay is exempt from that rule, so it gets a confirmation dialog instead.
          const promptMissingDiagnosisCodes = !sourceLoading && isSelfPay && values.billingCodes.length === 0
          return (
            <Form>
              {(status === 'draft' || dirty) && <DraftIndicator dirty={dirty} />}
              <TreatmentPlanFields />
              <Box justifyContent="flex-end" display="flex" mt={4}>
                {promptMissingDiagnosisCodes ? (
                  <ConfirmNoteDialog
                    isSubmitting={isSubmitting}
                    onSubmit={submitForm}
                    buttonLabel="Add"
                    dialogTitle={MISSING_DIAGNOSIS_TITLE}
                    dialogContents={MISSING_DIAGNOSIS_CONTENTS}
                  />
                ) : (
                  <SubmitButton isSubmitting={isSubmitting} disabled={sourceLoading} />
                )}
              </Box>
              <FormPersistor uid={uid} formType={FORM_TYPE} />
            </Form>
          )
        }}
      </Formik>
    </Grid>
  )
}

export default TreatmentPlanForm
